import { NavLink, useLocation } from "react-router-dom";
import {
  Home,
  ShoppingBag,
  TrendingUp,
  GraduationCap,
  User,
  Sprout,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useApp } from "@/context/AppContext";
import { UserAvatar } from "@/components/UserAvatar";
import logoMombongo from "@/assets/logo-mombongo.png";

const ROLE_BADGE = {
  investor: { label: "Investisseur", cls: "bg-blue-100 text-blue-700" },
  farmer:   { label: "Agriculteur",  cls: "bg-green-100 text-green-700" },
  agent:    { label: "Agent terrain", cls: "bg-amber-100 text-amber-700" },
  merchant: { label: "Commerçant",   cls: "bg-purple-100 text-purple-700" },
} as const;

const tabs = [
  { to: "/home", icon: Home, key: "home" },
  { to: "/market", icon: ShoppingBag, key: "market" },
  { to: "/bourse", icon: TrendingUp, key: "bourse" },
  { to: "/academia", icon: GraduationCap, key: "academia" },
  { to: "/profile", icon: User, key: "profile" },
] as const;

function shouldHide(pathname: string) {
  return (
    pathname === "/" ||
    pathname.startsWith("/language") ||
    pathname.startsWith("/auth") ||
    pathname.startsWith("/onboarding") ||
    /^\/(market|bourse|financement|academia)\/.+/.test(pathname)
  );
}

export function BottomNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  if (shouldHide(pathname)) return null;

  return (
    <nav className="lg:hidden shrink-0 z-30 bg-white border-t border-gray-200 px-2 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
      <ul className="grid grid-cols-5 max-w-[430px] mx-auto">
        {tabs.map(({ to, icon: Icon, key }) => (
          <li key={to}>
            <NavLink
              to={to}
              aria-label={t(`nav.${key}`)}
              data-testid={key === "profile" ? "profile-menu" : `nav-${key}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-1.5 rounded-lg transition ${
                  isActive ? "text-green-700" : "text-gray-400"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className="w-[22px] h-[22px]"
                    strokeWidth={isActive ? 2.5 : 1.8}
                  />
                  <span
                    className={`text-[10px] font-display ${isActive ? "font-bold" : "font-medium"}`}
                  >
                    {t(`nav.${key}`)}
                  </span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/* Desktop top navigation — only on lg+ screens */
export function TopNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { user, userProfile } = useAuth();
  const { role } = useApp();
  const displayName = user?.displayName || userProfile?.displayName || "";
  const badge = ROLE_BADGE[role] ?? ROLE_BADGE.investor;
  if (shouldHide(pathname)) return null;

  const allTabs = [
    ...tabs,
    { to: "/financement", icon: Sprout, key: "finance" } as const,
  ];

  return (
    <header className="hidden lg:block sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-8">
        <NavLink to="/home" className="flex items-center">
          <img
            src={logoMombongo}
            alt="Mombongo Coopérative"
            className="h-9 w-auto object-contain"
          />
        </NavLink>

        <nav className="flex items-center gap-1 flex-1">
          {allTabs.map(({ to, icon: Icon, key }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3.5 py-2 rounded-lg text-[13px] font-display font-bold transition ${
                  isActive
                    ? "bg-green-50 text-green-700"
                    : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={2.2} />
              {key === "finance" ? t("home.actions.finance") : t(`nav.${key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[12px] text-gray-700 font-semibold leading-tight">{displayName}</p>
            <span className={`text-[9px] font-extrabold rounded-full px-2 py-0.5 ${badge.cls}`}>
              {badge.label}
            </span>
          </div>
          <UserAvatar
            photoURL={user?.photoURL}
            displayName={displayName}
            className="w-9 h-9 rounded-xl text-[13px]"
          />
        </div>
      </div>
    </header>
  );
}
